import { Organization } from './organization';

export type Catalog = {
  '@nextLink': string;
  count: number;
  total: number;
  value: Array<Product>;
};

export type GeoCoordinatesField = {
  coordinates: string[];
  type: string;
};

export type Attribute = {
  categoryIds: string[];
  createdAt: string;
  customFields: object;
  delFlg: number;
  groupName: string;
  id: string;
  items?: {
    groupName: string;
    key: string;
    value: string;
  }[];
  name: string;
  order: number;
  type: 'Boolean' | 'Text' | 'Number' | 'Select' | 'MultiSelect';
  updatedAt: string;
};

export type AttributeListResponse = {
  '@nextLink': string;
  count: number;
  total: number;
  value: Attribute[];
};

export type ProductAttribute = {
  attributeId: string;
  value: string;
};

export type ProductImage = {
  id: string;
  type: string;
  url: string;
};

export type Product = {
  additionalInformation: string;
  attributes: ProductAttribute[];
  category: Category;
  categoryId: string;
  customFields: ProductCustomFields;
  description: string;
  dimensions: string; // NOTE:未使用
  features: ProductFeature[]; // NOTE:未使用
  geoCoordinatesField?: GeoCoordinatesField; // NOTE:未使用
  id: string;
  imageIds: string[];
  images?: ProductImage[];
  locations: ProductLocation[];
  name: string;
  organization?: Partial<Organization>;
  organizationId: string;
  publication: ProductPublication;
  tags: string[]; // NOTE:未使用
  variantNote: string; // NOTE:未使用
  variants: ProductVariant[];
};

export type ProductOverWorkConditions =
  | '時間調整可'
  | '残業あり'
  | '残業代の追加あり';

export enum ScheduleType {
  DAY = 'day',
  WEEK = 'week',
}

export type ProductCustomFields = {
  access: string;
  canBookToday: boolean;
  days: string[];
  endTime: string;
  maxBookingPeople: number;
  maxReservations: number;
  minBookingPeople: number;
  originPrice: number | string;
  prRules: string;
  price: string;
  repeatWeek: number[];
  reservationApproval: boolean;
  reservationNotes: string;
  scheduleType: ScheduleType;
  startTime: string;
  workAddress1: string;
  workAddress2: string;
  workPostalCode: string;
};

export type ProductLocation = {
  code: string;
  createdAt: string;
  id: string;
  name: string;
  parent: string;
  type: SupportedLocationType;
  updatedAt: string;
};

export type ProductInput = Omit<
  Product,
  | 'category'
  | 'geoCoordinatesField'
  | 'id'
  | 'images'
  | 'locations'
  | 'organizationId'
  | 'publication'
  | 'variants'
> & {
  geoCoordinatesField?: GeoLocationPointInput; // NOTE:未使用
  imageIds: string[]; // NOTE:未使用
  // images: ProductImage[];
  locationIds: string[];
  publication: ProductPublicationInput;
  variants: ProductVariantInput[];
};

export type ProductUpdate = Partial<
  Omit<
    Product,
    | 'category'
    | 'customFields'
    | 'geoCoordinatesField'
    | 'id'
    | 'images'
    | 'locations'
    | 'organizationId'
    | 'publication'
    | 'variants'
  >
> & {
  customFields?: Partial<ProductCustomFields>;
  geoCoordinatesField?: GeoLocationPointInput; // NOTE:未使用
  imageIds?: string[]; // NOTE:未使用
  locationIds?: string[];
  publication?: Partial<ProductPublicationInput>;
  variants?: ProductVariantUpdate[];
};

export type GeoLocationPointInput = {
  coordinates: number[];
  type: 'Point';
};

export type ProductImageInput = {
  alt: string;
  base64: string;
  variantIds: Array<string>;
};

export type ProductForm = {
  additionalInformation: string;
  categoryId: string;
  description: string;
  dimensions: string;
  endPeriod: string;
  features: Array<ProductFeature>;
  imageIds: string[];
  latitude?: number;
  locations: Array<ProductLocation>;
  longitude?: number;
  plans: Array<Plan>;
  startPeriod: string;
  status: SupportedProductPublicationStatus;
  title: string;
  variantNote: string;
};

export type ProductFeature = {
  name: string;
  value: string;
};

export type Price = {
  amount: number;
  currency: string;
  taxIncluded: boolean;
};

export type Plan = {
  id?: string;
  price: string;
  title: string;
};

export type ProductVariant = {
  description?: string;
  id: string;
  price: Price;
  sku?: 'day' | 'hour';
  title: string;
};

export type ProductVariantInput = Omit<ProductVariant, 'id' | 'title'>;

export type ProductVariantUpdate = Partial<ProductVariantInput>;

export const ProductPublicationStatus = {
  ACTIVE: 'ACTIVE',
  ARCHIVED: 'ARCHIVED',
  DRAFT: 'DRAFT',
} as const;

export type SupportedProductPublicationStatus =
  (typeof ProductPublicationStatus)[keyof typeof ProductPublicationStatus];

export type ProductPublication = {
  publishedAt: string;
  publishedBy: string;
  since: string;
  status: SupportedProductPublicationStatus;
  until: string;
};
export type ProductPublicationInput = Partial<
  Omit<ProductPublication, 'publishedAt' | 'publishedBy'>
>;

export interface CategoryResponse {
  '@nextLink': string;
  count: number;
  total: number;
  value: Array<Category>;
}

export interface Category {
  children: Category[];
  description: string;
  id?: string;
  name: string;
  numberOfProducts?: number;
  parent: null | {
    uri: string;
  };
  status: 'PENDING' | 'ACCEPTED' | 'PROCESSING' | 'ACTIVE';
}
export interface SubCategory {
  description: string;
  id?: string;
  name: string;
  numberOfProducts?: number;
  status: string;
}
export type CategoryInput = Omit<Category, 'id'>;

export interface LocationListResponse {
  '@nextLink': string;
  count: number;
  total: number;
  value: Array<Location>;
}

//TODO: Refactor type to have a sub-location structure like in Category
export interface Location {
  code: string;
  createdAt: string;
  id: string;
  name: string;
  parent: string;
  type: SupportedLocationType;
  updatedAt: string;
}

export const LocationType = {
  CITY: 'city',
  PREFECTURE: 'prefecture',
  REGION: 'region',
} as const;

export type SupportedLocationType =
  (typeof LocationType)[keyof typeof LocationType];

export const ProductStatus = {
  ACTIVE: 'ACTIVE',
  ARCHIVED: 'ARCHIVED',
  DRAFT: 'DRAFT',
} as const;

export type SupportedProductStatus =
  (typeof ProductStatus)[keyof typeof ProductStatus];

export const CategoryStatus = {
  ACTIVE: 'ACTIVE',
  ARCHIVED: 'ARCHIVED',
} as const;

export type SupportedCategoryStatus =
  (typeof CategoryStatus)[keyof typeof CategoryStatus];

/**
 * attachment
 */
export interface Attachment {
  id?: string;
  ownerId?: string;
  ownerType?: string;
  type?: string;
  url?: string;
}

export interface AttachmentCreation {
  objectId?: string;
  ownerId?: string;
  type?: string;
}
